<template>
  <div class="quiz-host-interface">

    <!-- Quiz Control Header -->
    <div class="quiz-control-header mb-4">
      <div class="row align-items-center">
        <div class="col">
          <h3 class="mb-0">
            <i class="bi bi-question-circle"></i>
            Quiz Control
          </h3>
          <small class="text-muted">{{ quizConfig?.name || 'Quiz Game' }}</small>
        </div>
        <div class="col-auto">
          <div class="quiz-status badge fs-6" :class="statusBadgeClass">
            {{ gameStatus }}
          </div>
        </div>
      </div>
    </div>

    <!-- Screen Navigation -->
    <div class="screen-navigation mb-4">
      <div class="card">
        <div class="card-body">
          <h5 class="card-title">
            <i class="bi bi-collection"></i>
            Screen Navigation
          </h5>

          <div class="current-screen-info mb-3">
            <div class="row align-items-center">
              <div class="col">
                <strong>Current Screen: {{ currentScreenIndex + 1 }} / {{ totalScreens }}</strong>
                <br>
                <small class="text-muted">
                  {{ currentScreen?.screenType === 'question' ? 'Question' : 'Interlude' }}
                  {{ currentScreen?.title ? `: ${currentScreen.title}` : '' }}
                </small>
              </div>
              <div class="col-auto">
                <div class="btn-group" role="group">
                  <button
                    @click="previousScreen"
                    :disabled="currentScreenIndex === 0 || gameStatus === 'stopped'"
                    class="btn btn-outline-secondary"
                  >
                    <i class="bi bi-chevron-left"></i>
                  </button>
                  <button
                    @click="nextScreen"
                    :disabled="currentScreenIndex >= totalScreens - 1 || gameStatus === 'stopped'"
                    class="btn btn-outline-secondary"
                  >
                    Next <i class="bi bi-chevron-right"></i>
                  </button>
                  <button
                    @click="revealAnswers"
                    :disabled="!canRevealAnswers || gameStatus === 'stopped'"
                    class="btn btn-outline-warning"
                    title="Reveal correct answers to players"
                  >
                    <i class="bi bi-eye"></i> Reveal
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- Screen Timeline -->
          <div class="screen-timeline">
            <div class="progress mb-2" style="height: 8px;">
              <div
                class="progress-bar"
                :style="{ width: `${screenProgress}%` }"
              ></div>
            </div>
            <div class="screen-indicators">
              <button
                v-for="(screen, index) in quizConfig?.screens || []"
                :key="index"
                @click="goToScreen(index)"
                :class="[
                  'screen-indicator',
                  { 'current': index === currentScreenIndex },
                  { 'completed': index < currentScreenIndex },
                  { 'question': screen.screenType === 'question' },
                  { 'interlude': screen.screenType === 'interlude' }
                ]"
                :title="getScreenTooltip(screen, index)"
              >
                <i :class="getScreenIcon(screen)"></i>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Game Control Buttons -->
    <div class="game-controls mb-4">
      <div class="card">
        <div class="card-body">
          <h5 class="card-title">
            <i class="bi bi-play-circle"></i>
            Game Controls
          </h5>

          <div class="btn-group w-100" role="group">
            <button
              v-if="gameStatus === 'stopped'"
              @click="startQuiz"
              class="btn btn-success"
            >
              <i class="bi bi-play-fill"></i>
              Start Quiz
            </button>
            <button
              v-if="gameStatus === 'playing'"
              @click="pauseQuiz"
              class="btn btn-warning"
            >
              <i class="bi bi-pause-fill"></i>
              Pause
            </button>
            <button
              v-if="gameStatus === 'paused'"
              @click="resumeQuiz"
              class="btn btn-primary"
            >
              <i class="bi bi-play-fill"></i>
              Resume
            </button>
            <button
              v-if="gameStatus !== 'stopped'"
              @click="stopQuiz"
              class="btn btn-danger"
            >
              <i class="bi bi-stop-fill"></i>
              Stop
            </button>
          </div>

          <!-- Timer Control (if applicable) -->
          <div v-if="currentScreen?.timeLimit" class="timer-control mt-3">
            <div class="d-flex align-items-center justify-content-between">
              <span><i class="bi bi-clock"></i> Timer: {{ formatTime(timeRemaining) }}</span>
              <button
                @click="skipTimer"
                :disabled="gameStatus !== 'playing'"
                class="btn btn-sm btn-outline-secondary"
              >
                Skip Timer
              </button>
            </div>
            <div class="progress mt-2" style="height: 6px;">
              <div
                class="progress-bar"
                :style="{ width: `${timerProgress}%` }"
              ></div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Player Answers Monitor -->
    <div class="player-answers-monitor mb-4">
      <div class="card">
        <div class="card-body">
          <h5 class="card-title">
            <i class="bi bi-people"></i>
            Player Answers
            <span class="badge bg-secondary">{{ answeredCount }} / {{ totalPlayers }}</span>
          </h5>

          <div v-if="currentScreen?.screenType === 'question'" class="question-monitor">
            <div class="question-info mb-3">
              <strong>{{ currentScreen.title || currentScreen.content }}</strong>
              <small class="text-muted d-block">
                {{ currentScreen.multipleSelection ? 'Multiple Selection' : 'Single Selection' }}
                • {{ currentScreen.pointValue || quizConfig?.defaultPointValue || 10 }} points
              </small>
            </div>

            <!-- Option Statistics -->
            <div class="option-statistics mb-3">
              <div
                v-for="(option, index) in currentScreen.options"
                :key="index"
                class="option-stat"
              >
                <div class="d-flex justify-content-between align-items-center">
                  <span class="option-text">
                    {{ option.text }}
                    <i v-if="option.correct" class="bi bi-check-circle text-success ms-1"></i>
                  </span>
                  <span class="option-count badge bg-primary">
                    {{ getOptionCount(option.value) }}
                  </span>
                </div>
                <div class="progress mt-1" style="height: 4px;">
                  <div
                    class="progress-bar"
                    :class="{ 'bg-success': option.correct }"
                    :style="{ width: `${getOptionPercentage(option.value)}%` }"
                  ></div>
                </div>
              </div>
            </div>
          </div>

          <!-- Player List -->
          <div class="players-list">
            <div class="row">
              <div
                v-for="player in connectedPlayers"
                :key="player.id"
                class="col-md-6 col-lg-4 mb-2"
              >
                <div class="player-card" :class="getPlayerCardClass(player.id)">
                  <div class="d-flex justify-content-between align-items-center">
                    <span class="player-name">{{ player.name }}</span>
                    <div class="player-status">
                      <span class="score badge bg-info">{{ getPlayerScore(player.id) }}</span>
                      <i :class="getPlayerStatusIcon(player.id)"></i>
                    </div>
                  </div>
                  <small class="player-answer text-muted">
                    {{ getPlayerAnswerDisplay(player.id) }}
                  </small>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Quiz Progress Summary -->
    <div class="quiz-summary">
      <div class="card">
        <div class="card-body">
          <h5 class="card-title">
            <i class="bi bi-graph-up"></i>
            Quiz Summary
          </h5>

          <div class="row text-center">
            <div class="col-md-3 mb-3">
              <div class="summary-stat">
                <div class="stat-value">{{ currentScreenIndex + 1 }}</div>
                <div class="stat-label">Current Screen</div>
              </div>
            </div>
            <div class="col-md-3 mb-3">
              <div class="summary-stat">
                <div class="stat-value">{{ answeredCount }}</div>
                <div class="stat-label">Players Answered</div>
              </div>
            </div>
            <div class="col-md-3 mb-3">
              <div class="summary-stat">
                <div class="stat-value">{{ averageScore.toFixed(0) }}</div>
                <div class="stat-label">Average Score</div>
              </div>
            </div>
            <div class="col-md-3 mb-3">
              <div class="summary-stat">
                <div class="stat-value">{{ quizProgress.toFixed(0) }}%</div>
                <div class="stat-label">Quiz Progress</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, onUnmounted } from 'vue'
import { QuizConfig } from '@/games/basic-quiz/models/QuizConfig'
import { QuizScreen } from '@/games/basic-quiz/models/QuizScreen'

// Props
interface Props {
  quizConfig: QuizConfig
  connectedPlayers: any[]
  signaling: any
  hostSignaling?: any
  currentScreenIndex?: number
  gameStatus?: 'stopped' | 'playing' | 'paused'
  playerAnswers?: Map<string, any>
  playerScores?: Map<string, number>
}

const props = withDefaults(defineProps<Props>(), {
  currentScreenIndex: 0,
  gameStatus: 'stopped',
  playerAnswers: () => new Map(),
  playerScores: () => new Map()
})

// Emits
const emit = defineEmits<{
  'screen-changed': [screenIndex: number]
  'quiz-started': []
  'quiz-paused': []
  'quiz-resumed': []
  'quiz-stopped': []
  'timer-skipped': []
  'reveal-answers': [screenIndex: number, correctAnswers: any]
}>()

// Reactive state - use props for shared state, local only for UI-specific state
const gameStatus = computed(() => props.gameStatus || 'stopped')
const currentScreenIndex = computed(() => props.currentScreenIndex || 0)
const playerAnswers = computed(() => props.playerAnswers || new Map())
const playerScores = computed(() => props.playerScores || new Map())
const timeRemaining = ref(0)
const timerInterval = ref<NodeJS.Timeout | null>(null)

// Computed properties
const totalScreens = computed(() => props.quizConfig?.screens?.length || 0)
const currentScreen = computed(() => props.quizConfig?.screens?.[currentScreenIndex.value])
const totalPlayers = computed(() => props.connectedPlayers?.length || 0)

const answeredCount = computed(() => {
  if (currentScreen.value?.screenType !== 'question') return 0
  return Array.from(playerAnswers.value.values())
    .filter(answer => answer.screenIndex === currentScreenIndex.value && answer.selections?.length > 0)
    .length
})

const screenProgress = computed(() => {
  if (totalScreens.value === 0) return 0
  return ((currentScreenIndex.value + 1) / totalScreens.value) * 100
})

const averageScore = computed(() => {
  const scores = Array.from(playerScores.value.values())
  if (scores.length === 0) return 0
  return scores.reduce((sum, score) => sum + score, 0) / scores.length
})

const quizProgress = computed(() => {
  if (totalScreens.value === 0) return 0
  return (currentScreenIndex.value / totalScreens.value) * 100
})

const statusBadgeClass = computed(() => {
  switch (gameStatus.value) {
    case 'playing': return 'bg-success'
    case 'paused': return 'bg-warning'
    default: return 'bg-secondary'
  }
})

const timerProgress = computed(() => {
  if (!currentScreen.value?.timeLimit) return 100
  return (timeRemaining.value / currentScreen.value.timeLimit) * 100
})

const canRevealAnswers = computed(() => {
  // Can reveal answers if we're on a question screen and some players have answered
  return currentScreen.value?.screenType === 'question' &&
         answeredCount.value > 0 &&
         gameStatus.value !== 'stopped'
})

// Methods
function startQuiz() {
  if (props.hostSignaling?.startGame) {
    props.hostSignaling.startGame(0, 'basic-quiz', props.quizConfig)
  }
  startScreenTimer()
  emit('quiz-started')
  emit('screen-changed', 0)
}

function pauseQuiz() {
  if (props.hostSignaling?.pauseGame) {
    props.hostSignaling.pauseGame()
  }
  stopTimer()
  emit('quiz-paused')
}

function resumeQuiz() {
  if (props.hostSignaling?.resumeGame) {
    props.hostSignaling.resumeGame()
  }
  startScreenTimer()
  emit('quiz-resumed')
}

function stopQuiz() {
  if (props.hostSignaling?.endGame) {
    props.hostSignaling.endGame(0)
  }
  stopTimer()
  emit('quiz-stopped')
}

function previousScreen() {
  if (props.hostSignaling?.previousScreen) {
    props.hostSignaling.previousScreen()
  }
  // Also emit for any local listeners
  emit('screen-changed', Math.max(0, currentScreenIndex.value - 1))
}

function nextScreen() {
  if (props.hostSignaling?.nextScreen) {
    props.hostSignaling.nextScreen()
  }
  // Also emit for any local listeners
  emit('screen-changed', Math.min(totalScreens.value - 1, currentScreenIndex.value + 1))
}

function goToScreen(index: number) {
  if (index >= 0 && index < totalScreens.value) {
    if (props.hostSignaling?.changeScreen) {
      props.hostSignaling.changeScreen(0, index, props.quizConfig.screens[index])
    }
    startScreenTimer()
    emit('screen-changed', index)
  }
}

function startScreenTimer() {
  stopTimer()

  const screen = currentScreen.value
  if (screen?.timeLimit && gameStatus.value === 'playing') {
    timeRemaining.value = screen.timeLimit

    timerInterval.value = setInterval(() => {
      timeRemaining.value--

      if (timeRemaining.value <= 0) {
        onTimerExpired()
      }
    }, 1000)
  }
}

function stopTimer() {
  if (timerInterval.value) {
    clearInterval(timerInterval.value)
    timerInterval.value = null
  }
}

function skipTimer() {
  timeRemaining.value = 0
  onTimerExpired()
  emit('timer-skipped')
}

function revealAnswers() {
  if (!canRevealAnswers.value) return

  // Get the correct answers for the current screen
  const screen = currentScreen.value
  if (!screen || screen.screenType !== 'question' || !screen.options) return

  const correctAnswers = {
    correctOptionId: screen.correctOptionId,
    correctOptions: screen.options.filter(option => option.correct)
  }

  emit('reveal-answers', currentScreenIndex.value, correctAnswers)
}

function onTimerExpired() {
  stopTimer()

  // Auto-advance to next screen if configured
  if (props.quizConfig?.nextScreenTrigger === 'timer') {
    nextScreen()
  }
}

function formatTime(seconds: number): string {
  return `${seconds}s`
}

function getScreenIcon(screen: QuizScreen): string {
  return screen.screenType === 'question' ? 'bi bi-question-circle' : 'bi bi-info-circle'
}

function getScreenTooltip(screen: QuizScreen, index: number): string {
  const type = screen.screenType === 'question' ? 'Question' : 'Interlude'
  const title = screen.title ? `: ${screen.title}` : ''
  return `Screen ${index + 1} - ${type}${title}`
}

function getPlayerCardClass(playerId: string): string {
  const hasAnswered = playerAnswers.value.get(playerId)?.screenIndex === currentScreenIndex.value
  return hasAnswered ? 'answered' : 'waiting'
}

function getPlayerStatusIcon(playerId: string): string {
  const hasAnswered = playerAnswers.value.get(playerId)?.screenIndex === currentScreenIndex.value
  return hasAnswered ? 'bi bi-check-circle text-success' : 'bi bi-clock text-muted'
}

function getPlayerScore(playerId: string): number {
  return playerScores.value.get(playerId) || 0
}

function getPlayerAnswerDisplay(playerId: string): string {
  const answer = playerAnswers.value.get(playerId)
  if (!answer || answer.screenIndex !== currentScreenIndex.value) {
    return 'No answer yet'
  }

  if (currentScreen.value?.screenType === 'question') {
    return answer.selections?.join(', ') || 'No selection'
  }

  return 'Viewing'
}

function getOptionCount(optionValue: string | boolean): number {
  return Array.from(playerAnswers.value.values())
    .filter(answer =>
      answer.screenIndex === currentScreenIndex.value &&
      answer.selections?.includes(optionValue.toString())
    ).length
}

function getOptionPercentage(optionValue: string | boolean): number {
  if (totalPlayers.value === 0) return 0
  const count = getOptionCount(optionValue)
  return (count / totalPlayers.value) * 100
}

// Handle player answer updates
function updatePlayerAnswer(playerId: string, answer: any) {
  playerAnswers.value.set(playerId, answer)
}

function updatePlayerScore(playerId: string, score: number) {
  playerScores.value.set(playerId, score)
}

// Public API
function getCurrentScreenData() {
  return {
    screenIndex: currentScreenIndex.value,
    screen: currentScreen.value,
    gameStatus: gameStatus.value
  }
}

// Expose methods
defineExpose({
  startQuiz,
  pauseQuiz,
  resumeQuiz,
  stopQuiz,
  nextScreen,
  previousScreen,
  goToScreen,
  updatePlayerAnswer,
  updatePlayerScore,
  getCurrentScreenData
})

// Lifecycle
onMounted(() => {
  console.log('Quiz host interface mounted with config:', props.quizConfig)
})

onUnmounted(() => {
  stopTimer()
})

// Watch for external updates
watch(() => props.connectedPlayers, (newPlayers) => {
  // Initialize scores for new players
  newPlayers.forEach(player => {
    if (!playerScores.value.has(player.id)) {
      playerScores.value.set(player.id, 0)
    }
  })
}, { deep: true })
</script>

<style scoped>
.quiz-host-interface {
  padding: 20px;
}

.quiz-control-header {
  border-bottom: 2px solid #e9ecef;
  padding-bottom: 15px;
}

.screen-navigation .current-screen-info {
  background: #f8f9fa;
  padding: 15px;
  border-radius: 8px;
}

.screen-timeline {
  margin-top: 15px;
}

.screen-indicators {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  margin-top: 8px;
}

.screen-indicator {
  width: 40px;
  height: 40px;
  border: 2px solid #dee2e6;
  border-radius: 8px;
  background: #f8f9fa;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
}

.screen-indicator:hover {
  border-color: #6c757d;
  transform: translateY(-1px);
}

.screen-indicator.current {
  border-color: #0d6efd;
  background: #0d6efd;
  color: white;
}

.screen-indicator.completed {
  border-color: #198754;
  background: #198754;
  color: white;
}

.screen-indicator.question {
  /* Question screens use default styling */
  color: #6f42c1;
}

.screen-indicator.interlude {
  background: #ffc107;
  border-color: #ffc107;
  color: #000;
}

.option-stat {
  padding: 8px 0;
  border-bottom: 1px solid #e9ecef;
}

.option-stat:last-child {
  border-bottom: none;
}

.option-text {
  font-weight: 500;
}

.players-list {
  max-height: 300px;
  overflow-y: auto;
}

.player-card {
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  padding: 10px;
  transition: all 0.2s ease;
}

.player-card.answered {
  background: #d1edff;
  border-color: #0d6efd;
}

.player-card.waiting {
  background: #fff3cd;
  border-color: #ffc107;
}

.player-name {
  font-weight: 500;
  color: #495057;
}

.player-status {
  display: flex;
  align-items: center;
  gap: 8px;
}

.summary-stat {
  padding: 15px;
  background: #f8f9fa;
  border-radius: 8px;
}

.stat-value {
  font-size: 2rem;
  font-weight: bold;
  color: #495057;
}

.stat-label {
  color: #6c757d;
  font-size: 0.875rem;
  font-weight: 500;
}

.timer-control {
  background: #f8f9fa;
  padding: 15px;
  border-radius: 8px;
}

/* Mobile optimizations */
@media (max-width: 768px) {
  .quiz-host-interface {
    padding: 15px;
  }

  .screen-indicators {
    justify-content: center;
  }

  .screen-indicator {
    width: 35px;
    height: 35px;
  }

  .players-list .col-md-6,
  .players-list .col-lg-4 {
    flex: 1 1 100%;
  }
}
</style>
